/* eslint-disable no-await-in-loop */
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET } from 'common/constants/pdfExport';
import { NetworkMonitor } from 'common/types/network';
import { Logger } from 'utilities';
import {
  CONVERSION_RATIOS,
  IMAGE_SETTINGS,
  PROGRESS_MILESTONES,
  PROGRESS_PERCENTAGE,
  TEMPLATE_CONSTANTS,
  TIMEOUT_MS,
} from './constants';
import { PdfExportConfig, PdfExportResult } from './interfaces';
import { SmoothProgressAnimator } from './SmoothProgressAnimator'; 

/**
 * PDF Exporter class for handling multiple pages export
 */
export class PdfExporter {
  private pdf: JsPDF | null = null;
  private pageLogo: string | null = null;
  private infoIcon: string | null = null;
  private footerLogo: string | null = null;
  private networkMonitor: NetworkMonitor;

  constructor() {
    this.networkMonitor = new NetworkMonitor();
  }

  /**
   * Wait for all API calls to complete and pdfContentRef element to be present and loaded
   */
  private waitForApiCallsAndPdfContentElement = async (
    document: Document,
    maxWaitTime: number = 60000
  ): Promise<void> =>
    new Promise<void>(resolve => {
      const startTime = Date.now();

      // Setup network monitoring
      const { hasPendingRequests: hasPendingMonitoredRequests, cleanup } = this.networkMonitor.setupNetworkMonitoring();

      const checkForApiCallsAndElement = () => {
        // Check for pending network requests using multiple methods
        const hasPendingViaPerformance = this.networkMonitor.hasPendingNetworkRequests();
        const hasPendingViaMonitoring = hasPendingMonitoredRequests();

        if (hasPendingViaPerformance || hasPendingViaMonitoring) {
          Logger.info('Network requests still pending, waiting...');
          setTimeout(checkForApiCallsAndElement, TIMEOUT_MS.API_CHECK_RETRY);
          return;
        }

        // Get the query parameter from the document's URL
        const documentUrl = document.location?.href || document.defaultView?.location?.href;
        const reference = documentUrl
          ? new URL(documentUrl).searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET)
          : '';

        // Look for element with data-ref attribute
        const targetElement = document.querySelector(`[data-ref="${reference}"]`);

        if (targetElement) {
          Logger.info('Found target element in the DOM, checking if content is loaded...');

          // Check if element has content (not just skeleton)
          const hasContent = targetElement.children.length > 0;

          // Check for skeleton elements that might still be visible
          const skeletonElements = document.querySelectorAll(
            '[data-testid*="skeleton"], [data-testid*="loading"], ' +
              '.skeleton, .loading-skeleton, .shimmer, ' +
              '[class*="skeleton"], [class*="loading"], ' +
              '[aria-label*="loading"], [aria-label*="skeleton"]'
          );

          const hasVisibleSkeletons = Array.from(skeletonElements).some(element => {
            const style = window.getComputedStyle(element);
            return (
              style.display !== 'none' &&
              style.visibility !== 'hidden' &&
              style.opacity !== '0' &&
              (element as HTMLElement).offsetWidth > 0 &&
              (element as HTMLElement).offsetHeight > 0
            );
          });

          if (hasContent && !hasVisibleSkeletons) {
            Logger.info('Target element is loaded with content and no skeletons visible');
            Logger.info('Waiting 4 seconds for content to fully settle...');

            // Wait 4 seconds for content to fully settle after API calls are done
            setTimeout(() => {
              Logger.info('4-second delay completed, proceeding with PDF generation');
              cleanup(); // Clean up monitoring
              resolve();
            }, 4000);
            return;
          }

          if (hasVisibleSkeletons) {
            Logger.info('Skeleton elements still visible, waiting...');
          }
        }

        // Check if we've exceeded max wait time
        if (Date.now() - startTime > maxWaitTime) {
          Logger.warn('Timeout waiting for API calls and target element, proceeding anyway');
          cleanup(); // Clean up monitoring
          resolve();
          return;
        }

        // Check again in 500ms
        setTimeout(checkForApiCallsAndElement, 500);
      };

      checkForApiCallsAndElement();
    });

  /**
   * Load image from public folder and convert to base64 data URL
   */
  private static async loadImageAsDataUrl(imagePath: string): Promise<string> {
    try {
      const response = await fetch(imagePath);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      Logger.error('Error loading image:', error);
      throw error;
    }
  }

  /**
   * Get image dimensions from data URL
   */
  private static async getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * Convert Blob to binary string for jsPDF
   */
  private static blobToBinaryString(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(binary);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  private static async loadCustomFonts(pdf: JsPDF): Promise<void> {
    try {
      // Load Inter Regular
      const interRegularResponse = await fetch('/fonts/Inter-Regular.ttf');
      if (!interRegularResponse.ok) {
        throw new Error(`Failed to load Inter-Regular.ttf: ${interRegularResponse.statusText}`);
      }
      const interRegularBlob = await interRegularResponse.blob();
      const interRegularBinary = await PdfExporter.blobToBinaryString(interRegularBlob);
      
      pdf.addFileToVFS('Inter-Regular.ttf', interRegularBinary);
      pdf.addFont('Inter-Regular.ttf', 'Inter', 'normal');

      // Load Inter Italic
      const interItalicResponse = await fetch('/fonts/Inter-Italic.ttf');
      if (!interItalicResponse.ok) {
        throw new Error(`Failed to load Inter-Italic.ttf: ${interItalicResponse.statusText}`);
      }
      const interItalicBlob = await interItalicResponse.blob();
      const interItalicBinary = await PdfExporter.blobToBinaryString(interItalicBlob);
      
      pdf.addFileToVFS('Inter-Italic.ttf', interItalicBinary);
      pdf.addFont('Inter-Italic.ttf', 'Inter', 'italic');      

      // Load Inter ExtraLight
      const interExtraLightResponse = await fetch('/fonts/Inter-ExtraLight.ttf');
      if (!interExtraLightResponse.ok) {
        throw new Error(`Failed to load Inter-ExtraLight.ttf: ${interExtraLightResponse.statusText}`);
      }
      const interExtraLightBlob = await interExtraLightResponse.blob();
      const interExtraLightBinary = await PdfExporter.blobToBinaryString(interExtraLightBlob);
      pdf.addFileToVFS('Inter-ExtraLight.ttf', interExtraLightBinary);
      pdf.addFont('Inter-ExtraLight.ttf', 'Inter', 'extra-light');

      // Load Inter Bold      
      const interBoldResponse = await fetch('/fonts/Inter-Bold.ttf');
      if (interBoldResponse.ok) {
        const interBoldBlob = await interBoldResponse.blob();
        const interBoldBinary = await PdfExporter.blobToBinaryString(interBoldBlob);
        pdf.addFileToVFS('Inter-Bold.ttf', interBoldBinary);
        pdf.addFont('Inter-Bold.ttf', 'Inter', 'bold');
      }
      
      // Load Neuton Light
      const neutonResponse = await fetch('/fonts/Neuton-Light.ttf');
      if (!neutonResponse.ok) {
        throw new Error(`Failed to load Neuton-Light.ttf: ${neutonResponse.statusText}`);
      }
      const neutonBlob = await neutonResponse.blob();
      const neutonBinary = await PdfExporter.blobToBinaryString(neutonBlob);
      pdf.addFileToVFS('Neuton-Light.ttf', neutonBinary);
      pdf.addFont('Neuton-Light.ttf', 'Neuton', 'light');

      // Load Neuton Italic
      const neutonItalicResponse = await fetch('/fonts/Neuton-Italic.ttf');
      if (!neutonItalicResponse.ok) {
        throw new Error(`Failed to load Neuton-Italic.ttf: ${neutonItalicResponse.statusText}`);
      }
      const neutonItalicBlob = await neutonItalicResponse.blob();
      const neutonItalicBinary = await PdfExporter.blobToBinaryString(neutonItalicBlob);
      pdf.addFileToVFS('Neuton-Italic.ttf', neutonItalicBinary);
      pdf.addFont('Neuton-Italic.ttf', 'Neuton', 'italic');

      Logger.info('Custom fonts loaded successfully');

    } catch (error) {
      Logger.warn('Failed to load custom fonts', error);      
    }
  }

  /**
   * Generate cover page template
   */
  public async generateCover(config: PdfExportConfig): Promise<void> {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call export() first.');
    }
    const { coverBackgroundImage, coverTitle, entityName, dateOfIssue, coverLogo, coverSecondaryLogo, isFirmOwned } = config;

    // Background image
    this.pdf.addImage(coverBackgroundImage, IMAGE_SETTINGS.JPEG_FORMAT, 0, 0, this.pdf.internal.pageSize.getWidth(), this.pdf.internal.pageSize.getHeight());

    // Logo image setup
    const logoDimensions = await PdfExporter.getImageDimensions(coverLogo);      
    const logoHeightMm = logoDimensions.height / CONVERSION_RATIOS.MM_TO_PX;
    const pageHeight = this.pdf.internal.pageSize.getHeight();
    const pageWidth = this.pdf.internal.pageSize.getWidth();

    if (isFirmOwned) {            
      // Top left position
      this.pdf.addImage(
        coverLogo,
        IMAGE_SETTINGS.PNG_FORMAT,
        TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
        TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM,
        TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM,
        TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM
      );

      // Draw a horizontal line to the right of the logo, centered vertically, spanning to page margin
      const logoCenterY = TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM + TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM / 2;
      const lineXStart = TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM + TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM + TEMPLATE_CONSTANTS.COVER_LINE_LEFT_MARGIN_MM;
      const lineXEnd = pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(3, 125, 232);
      this.pdf.line(lineXStart, logoCenterY, lineXEnd, logoCenterY);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);    

      // Bottom right position: Line + Space + "Powered by Scalar" + Space + Logo
      const logoBottomRightCenterY = pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM / 2;
      
      // Logo position (rightmost)
      const logoBottomRightX = pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM - TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_WIDTH_MM;
      const logoBottomRightY = pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM;
      
      // Text position (before logo)
      this.pdf.setFont('Inter', 'extra-light');
      this.pdf.setFontSize(10);
      const textWidth = this.pdf.getTextWidth('Powered by');
      const textX = logoBottomRightX - TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_TEXT_SPACING_MM - textWidth;
      
      // Line position (before text)
      const lineBottomRightXStart = TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      const lineBottomRightXEnd = textX - TEMPLATE_CONSTANTS.COVER_SECONDARY_LINE_TEXT_SPACING_MM;
      
      // Logo bottom Y position for text alignment
      const logoBottomY = logoBottomRightY + TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM;
      
      // Draw line
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(3, 125, 232);
      this.pdf.line(lineBottomRightXStart, logoBottomRightCenterY, lineBottomRightXEnd, logoBottomRightCenterY);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);
      
      // Draw text (aligned to bottom of logo)
      this.pdf.setTextColor(3, 125, 232);
      this.pdf.text('Powered by', textX, logoBottomY);
      
      // Draw secondary logo
      this.pdf.addImage(
        coverSecondaryLogo,
        IMAGE_SETTINGS.PNG_FORMAT,
        logoBottomRightX,
        logoBottomRightY,
        TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_WIDTH_MM,
        TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM
      );
        
    } else {      
      // Bottom left position
      this.pdf.addImage(
        coverLogo,
        IMAGE_SETTINGS.PNG_FORMAT,
        TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
        pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - (logoHeightMm / 2),
        TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM,
        TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM
      );

      // Draw a horizontal line to the right of the logo, aligned at the bottom of the logo, spanning to page margin
      const logoY = pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - (logoHeightMm / 2);
      const logoBottomY = logoY + TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM;
      const lineXStart = TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM + TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM + TEMPLATE_CONSTANTS.COVER_LINE_LEFT_MARGIN_MM;
      const lineXEnd = pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(255, 255, 255);
      this.pdf.line(lineXStart, logoBottomY, lineXEnd, logoBottomY);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);
    }
    
    const halfHeight = this.pdf.internal.pageSize.getHeight() / 2;
    const xPosition = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const coverTitleY = halfHeight - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const entityNameY = halfHeight + TEMPLATE_CONSTANTS.COVER_TEXT_SEPARATOR_MM;
    const dateOfIssueY = halfHeight + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

    this.pdf.setTextColor(isFirmOwned ? 0 : 255, isFirmOwned ? 0 : 255, isFirmOwned ? 0 : 255);
    
    // Color for accent elements (line and dateOfIssue): #328E65 if isFirmOwned, otherwise #31EDA9
    const accentColor = isFirmOwned ? [50, 142, 101] : [49, 237, 169];
    
    this.pdf.setFont('Inter', 'italic');
    this.pdf.setFontSize(16);
    this.pdf.text(`${coverTitle}`, xPosition, coverTitleY);
    
    // Draw a line to the right of the coverTitle, aligned at the bottom
    const coverTitleWidth = this.pdf.getTextWidth(`${coverTitle}`);
    const lineXStart = xPosition + coverTitleWidth;
    const lineXEnd = lineXStart + TEMPLATE_CONSTANTS.COVER_TITLE_LINE_LENGTH_MM;
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    this.pdf.line(lineXStart, coverTitleY, lineXEnd, coverTitleY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    this.pdf.setFont('Neuton', 'light');
    this.pdf.setFontSize(40);
    this.pdf.text(`${entityName}`, xPosition, entityNameY);

    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    this.pdf.text(`${dateOfIssue}`.toUpperCase(), xPosition, dateOfIssueY);
    
    // Set page text color to #037DE8
    this.pdf.setTextColor(0, 3, 125, 232);
  }

  /**
   * Generate header template for a PDF page
   */
  public generateHeader(config: PdfExportConfig, pageTitle: string): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call export() first.');
    }
    const { entityName } = config;
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const headerY = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;

    // Logo dimensions: 75px width, 25px height converted to mm
    const logoWidthMm = 75 / CONVERSION_RATIOS.MM_TO_PX; // ~19.84mm
    const logoHeightMm = 25 / CONVERSION_RATIOS.MM_TO_PX; // ~6.62mm
    
    // Position logo at left margin, vertically centered
    const logoX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const logoY = headerY - logoHeightMm / 2;

    // Add page logo
    if (this.pageLogo) {
      this.pdf.addImage(
        this.pageLogo,
        IMAGE_SETTINGS.PNG_FORMAT,
        logoX,
        logoY,
        logoWidthMm,
        logoHeightMm
      );
    }

    // Set font properties for entityName measurement: Inter, Bold (700), 10px
    // Try to use Inter Bold, fallback to Inter Regular if not available
    
    this.pdf.setFont('Inter', 'bold');    
    this.pdf.setFontSize(10);
    
    // Text transform: uppercase
    const entityNameText = (entityName || '').toUpperCase();
    const entityNameWidth = this.pdf.getTextWidth(entityNameText);
    
    // Entity name positioned at right margin
    const entityNameSpacingMm = 3; // Space between line and entityName
    const entityNameX = pageWidth - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const entityNameY = headerY;
    
    // Line after logo, ending before entityName
    const lineSpacingMm = 3; // Space between logo and line
    const lineXStart = logoX + logoWidthMm + lineSpacingMm;
    const lineXEnd = entityNameX - entityNameSpacingMm - entityNameWidth;
    const lineY = headerY;
    
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(3, 125, 232); // #037DE8
    this.pdf.line(lineXStart, lineY, lineXEnd, lineY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Draw entity name
    this.pdf.setTextColor(3, 125, 232); // #037DE8
    this.pdf.text(entityNameText, entityNameX, entityNameY, { align: 'right' });    

    // Add page title below the header
    const titleSpacingBelowHeaderMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;
    const titleY = headerY + titleSpacingBelowHeaderMm;
    this.pdf.setFont('Neuton', 'italic');
    this.pdf.setFontSize(20);
    this.pdf.text(pageTitle || '', TEMPLATE_CONSTANTS.PAGE_MARGIN_MM, titleY, { align: 'left' });

    // Draw line below page title
    const titleLineWidthPx = 50;
    const titleLineHeightPx = 2;
    const titleLineWidthMm = titleLineWidthPx / CONVERSION_RATIOS.MM_TO_PX;
    const titleLineHeightMm = titleLineHeightPx / CONVERSION_RATIOS.MM_TO_PX;
    const titleLineSpacingMm = 2; // Space between title and line
    const titleLineY = titleY + titleLineSpacingMm;
    const titleLineX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    
    this.pdf.setLineWidth(titleLineHeightMm);
    this.pdf.setDrawColor(49, 237, 169); // #31EDA9
    this.pdf.line(titleLineX, titleLineY, titleLineX + titleLineWidthMm, titleLineY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Reset text color
    this.pdf.setTextColor(0, 0, 0);
  }

  /**
   * Generate footer template for a PDF page
   * Layout: firmName (or "Powered By" + logo) + line + status box + line + page number info
   */
  public generateFooter(
    currentPage: number,
    totalPages: number,
    firmName: string,
    status: number | undefined,
    isFirmOwned: boolean
  ): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call export() first.');
    }
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const pageHeight = this.pdf.internal.pageSize.getHeight();

    // Footer positioned at the bottom of the page
    const footerYMm = pageHeight - 15;
    const footerBaselineY = footerYMm;

    // Color constants
    const blueColor = [3, 125, 232]; // #037DE8
    
    // Determine status box background color based on status
    // Convert hex with alpha to RGB (approximate with lighter colors)
    let statusBoxBgColor: number[];
    
    if (status === 0) {
      // PUBLISHED: #19C2E71A (cyan/blue with alpha)
      statusBoxBgColor = [230, 248, 255]; // Light cyan to approximate rgba(25, 194, 231, 0.1)
    } else if (status === 1) {
      // FINAL: #FF3F3F1A (red with alpha)
      statusBoxBgColor = [255, 230, 230]; // Light red to approximate rgba(255, 63, 63, 0.1)
    } else {
      // DRAFT: #7777771A (grey with alpha)
      statusBoxBgColor = [245, 245, 245]; // Light grey to approximate rgba(119, 119, 119, 0.1)
    }
    
    // Text color is the same for all statuses
    const statusTextColor = [0, 74, 139]; // #004A8B (dark blue)

    // Font settings - all text uses same font and size
    const fontSize = 10;
    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(fontSize);

    // 1. Draw firmName or "Powered By" + logo on the left
    let leftElementWidth = 0;
    const leftElementX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const leftElementY = footerBaselineY + 1.5;

    if (isFirmOwned) {
      // Draw firmName
      const firmNameText = (firmName || '').toUpperCase();
      this.pdf.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
      this.pdf.text(firmNameText, leftElementX, leftElementY);
      leftElementWidth = this.pdf.getTextWidth(firmNameText);
    } else {
      // Draw "Powered By" + logo
      this.pdf.setFont('Inter', 'extra-light');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
      const poweredByText = 'Powered By';
      const poweredByWidth = this.pdf.getTextWidth(poweredByText);
      const textLogoSpacing = 2; // Space between text and logo
      
      // Draw "Powered By" text
      this.pdf.text(poweredByText, leftElementX, leftElementY);
      
      // Draw scalar logo next to text
      if (this.footerLogo) {
        const logoWidthMm = TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_WIDTH_MM;
        const logoHeightMm = TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM;
        const logoX = leftElementX + poweredByWidth + textLogoSpacing;
        const logoY = footerBaselineY - logoHeightMm / 2;
        
        this.pdf.addImage(
          this.footerLogo,
          IMAGE_SETTINGS.PNG_FORMAT,
          logoX,
          logoY,
          logoWidthMm,
          logoHeightMm
        );
        
        leftElementWidth = poweredByWidth + textLogoSpacing + logoWidthMm;
      } else {
        leftElementWidth = poweredByWidth;
      }
      
      // Reset font for rest of footer
      this.pdf.setFont('Inter', 'normal');
    }

    // Calculate positions for elements
    const elementSpacing = 3; // Space between elements
    const line1StartX = leftElementX + leftElementWidth + elementSpacing;

    // Determine status text
    let statusText = 'DRAFT - FOR DISCUSSION PURPOSE ONLY - SUBJECT TO CHANGE';

    if (status === 0) {
      // ALLOCATIONS_PUBLISHED
      statusText = 'PUBLISHED - SUBJECT TO CHANGE';
    }
    if (status === 1) {
      // ALLOCATIONS_FINAL
      statusText = 'FINAL';
    }

    // Calculate status box dimensions
    // Height: 24px converted to mm
    const statusBoxHeightMm = 24 / CONVERSION_RATIOS.MM_TO_PX; // Approximately 6.35mm
    
    const iconSize = 2.5; // Icon size in mm
    const iconPadding = 1.5; // Padding around icon
    const statusBoxPadding = 2; // Padding inside the box (horizontal)
    
    // Calculate text width for status text
    const statusTextWidth = this.pdf.getTextWidth(statusText);
    
    // Determine if icon should be shown (only for draft)
    const showIcon = status === -1 || status === undefined;
    
    // Calculate content width (icon + spacing + text)
    const contentWidth = showIcon ? iconSize + iconPadding + statusTextWidth : statusTextWidth;
    
    // Status box width = content + padding on both sides
    const statusBoxWidth = contentWidth + statusBoxPadding * 2;

    // Calculate page number text and position first to determine line positions
    const pageNumberText = `PAGE ${currentPage} OF ${totalPages}`;
    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
    const pageNumberWidth = this.pdf.getTextWidth(pageNumberText);
    const pageNumberX = pageWidth - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const pageNumberY = footerBaselineY;

    // Calculate line lengths - longer when status is not draft (closer to status box)
    const availableWidth = pageNumberX - pageNumberWidth - line1StartX - elementSpacing * 4; // Account for spacing
    const isDraft = status === -1 || status === undefined;
    
    // For non-draft statuses, make lines longer (closer to status box)
    // For draft, keep lines shorter
    const lineLength = isDraft 
      ? Math.min(availableWidth / 3, 30) // Shorter for draft
      : Math.min(availableWidth / 2.5, 40); // Longer for published/final
    
    // Position first line end
    const line1EndX = line1StartX + lineLength;
    
    // Position second line start (before page number)
    const line2EndX = pageNumberX - pageNumberWidth - elementSpacing;
    const line2StartX = line2EndX - lineLength;
    
    // Center status box between the two lines
    const statusBoxCenterX = (line1EndX + line2StartX) / 2;
    const statusBoxX = statusBoxCenterX - statusBoxWidth / 2;
    const statusBoxY = footerBaselineY - statusBoxHeightMm / 2;

    // 2. Draw first blue line (from firmName, shorter length)
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(blueColor[0], blueColor[1], blueColor[2]);
    this.pdf.line(line1StartX, footerBaselineY, line1EndX, footerBaselineY);

    // 3. Draw status box with background
    // Draw background rectangle with rounded corners
    this.pdf.setFillColor(statusBoxBgColor[0], statusBoxBgColor[1], statusBoxBgColor[2]);
    this.pdf.roundedRect(statusBoxX, statusBoxY, statusBoxWidth, statusBoxHeightMm, 1.5, 1.5, 'F');

    // Calculate centered positions for icon and text
    const contentStartX = statusBoxX + (statusBoxWidth - contentWidth) / 2; // Center content horizontally
    
    // Add info icon inside the box (only for draft, aligned with text baseline)
    if (showIcon && this.infoIcon) {
      const iconX = contentStartX;
      // Align icon center with text baseline (footerBaselineY)
      const iconY = footerBaselineY - iconSize / 2;
      this.pdf.addImage(this.infoIcon, IMAGE_SETTINGS.PNG_FORMAT, iconX, iconY, iconSize, iconSize);
    }

    // Add status text inside the box (centered horizontally, aligned with baseline)
    this.pdf.setTextColor(statusTextColor[0], statusTextColor[1], statusTextColor[2]);
    const statusTextX = showIcon ? contentStartX + iconSize + iconPadding : contentStartX;
    // Text baseline should align with footerBaselineY for proper vertical centering
    const statusTextY = footerBaselineY + 1.5;
    this.pdf.text(statusText, statusTextX, statusTextY);

    // Draw second blue line (shorter length, before page number)
    this.pdf.line(line2StartX, footerBaselineY, line2EndX, footerBaselineY);

    // 5. Draw page number info on the right
    this.pdf.text(pageNumberText, pageNumberX, pageNumberY, { align: 'right' });

    // Reset drawing settings
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);
    this.pdf.setTextColor(0, 0, 0);
  }

  /**
   * Execute crawl: render an iframe to capture DOM elements from a link
   */
  public async executeCrawl(
    link: { url: string; title: string },
    currentPage: number,
    totalPages: number,
    reportProgress: (progressWithinPage: number) => void
  ): Promise<HTMLCanvasElement | null> {
    const { url } = link;

    // Add page parameters to URL
    const urlWithPageParams = new URL(url);
    urlWithPageParams.searchParams.set('currentPage', currentPage.toString());
    urlWithPageParams.searchParams.set('totalPages', totalPages.toString());
    const finalUrl = urlWithPageParams.toString();

    Logger.info(`Processing page ${currentPage}/${totalPages}: ${finalUrl}`);

    // Report initial progress when starting to process a page
    reportProgress(PROGRESS_MILESTONES.INITIAL);

    // Create a hidden iframe to load the page so the user doesn't see the navigation
    const iframe = document.createElement('iframe');
    iframe.src = finalUrl;
    iframe.style.position = 'fixed';
    iframe.style.zIndex = '9999';
    iframe.style.left = '-9999px';
    iframe.style.width = '2600px';    
    iframe.style.height = '5000px';

    Logger.info('Created iframe, adding to DOM');

    document.body.appendChild(iframe);

    try {
      // Wait for iframe to load
      Logger.info('Waiting for iframe to load...');
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          Logger.error(`Timeout loading ${url}`, new Error(`Timeout loading ${url}`));
          reject(new Error(`Timeout loading ${url}`));
        }, TIMEOUT_MS.IFRAME_LOAD);

        iframe.onload = () => {
          clearTimeout(timeout);
          reportProgress(PROGRESS_MILESTONES.AFTER_IFRAME_LOAD);
          resolve();
        };

        iframe.onerror = () => {
          Logger.error(`Failed to load ${url}`, new Error(`Failed to load ${url}`));
          clearTimeout(timeout);
          reject(new Error(`Failed to load ${url}`));
        };
      });

      // Get iframe document reference
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error(`Could not access iframe content for ${url}`);
      }

      Logger.info('Waiting for API calls to complete and target element in the DOM to load...');
      reportProgress(PROGRESS_MILESTONES.BEFORE_API_WAIT);
      await this.waitForApiCallsAndPdfContentElement(iframeDoc);
      reportProgress(PROGRESS_MILESTONES.AFTER_API_COMPLETE);

      // Find the element to export
      const domElementRef = urlWithPageParams.searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET);
      const pdfContentElement = iframeDoc.querySelector(`[data-ref="${domElementRef}"]`);

      // Capture content
      reportProgress(PROGRESS_MILESTONES.CAPTURING_CONTENT);
      if (pdfContentElement) {
        const element = pdfContentElement as HTMLElement;
        const contentCanvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: 'transparent',
          windowWidth: 2600,
          windowHeight: Math.max(element.scrollHeight, element.offsetHeight),
          onclone: (_clonedDoc, clonedElement) => {
            // Remove overflow clipping to prevent text from being cut in half.
            // 1. Scroll containers - allow full horizontal content to be visible
            const scrollContainerSelectors = [
              '[data-testid="native-scroll-wrapper"]',
              '[data-testid="native-scroll-content"]',
              '#spreadsheet-table__content-columns',
            ];
            scrollContainerSelectors.forEach(selector => {
              clonedElement.querySelectorAll(selector).forEach(el => {
                const htmlEl = el as HTMLElement;
                if (htmlEl.style) {
                  htmlEl.style.overflow = 'visible';
                  htmlEl.style.overflowX = 'visible';
                  htmlEl.style.overflowY = 'visible';
                  if (selector === '#spreadsheet-table__content-columns') {
                    htmlEl.style.maxWidth = 'none';
                  }
                }
              });
            });
            // 2. Table cells - remove overflow clipping from CSS (overflow: hidden, text-overflow: ellipsis)
            //    Preserve vertical-align to avoid spacing issues
            clonedElement.querySelectorAll('td, th').forEach(el => {
              const htmlEl = el as HTMLElement;
              if (htmlEl.style) {
                htmlEl.style.overflow = 'visible';
                htmlEl.style.overflowX = 'visible';
                htmlEl.style.overflowY = 'visible';
                htmlEl.style.textOverflow = 'clip';
                htmlEl.style.verticalAlign = 'middle';
              }
            });
            // 3. Cell content (.cell, .text-cell) - these have overflow: hidden that clips text
            clonedElement.querySelectorAll('.cell, .text-cell').forEach(el => {
              const htmlEl = el as HTMLElement;
              if (htmlEl.style) {
                htmlEl.style.overflow = 'visible';
                htmlEl.style.overflowX = 'visible';
                htmlEl.style.overflowY = 'visible';
                htmlEl.style.textOverflow = 'clip';
                htmlEl.style.webkitLineClamp = 'unset';
              }
            });
          },
        });
        reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE);
        return contentCanvas;
      }
      Logger.info(
        `Could not find target element [${domElementRef}] in the DOM for page ${currentPage}, skipping page...`
      );
      reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE);
      return null;
    } catch (error) {
      Logger.error(`Error processing URL ${url}`, error);
      reportProgress(PROGRESS_MILESTONES.PAGE_COMPLETE);
      return null;
    } finally {
      // Clean up iframe
      Logger.info('Cleaning up iframe...');
      try {
        document.body.removeChild(iframe);
      } catch (cleanupError) {
        Logger.warn('Error cleaning up iframe:', cleanupError);
      }
    }
  }

  /**
   * Export multiple links as a single PDF
   */
  public async export(config: PdfExportConfig): Promise<PdfExportResult> {
    try {
      Logger.info('PDF Export initiated with config:', config);
      const { pages: links, onProgress } = config;

      if (!links || links.length === 0) {
        Logger.error('No links provided for PDF export', new Error('No links provided for PDF export'));
        return { success: false, error: 'No links provided for PDF export' };
      }

      Logger.info(`Starting export of ${links.length} links`);

      // Store canvas data for each page to create robust final PDF
      const canvases: (HTMLCanvasElement | null)[] = [];

      // Create smooth progress animator
      let globalProgressAnimator: SmoothProgressAnimator | null = null;
      if (onProgress) {
        globalProgressAnimator = new SmoothProgressAnimator((progress: number) => {
          const currentPageNum = Math.ceil((progress / 100) * links.length) || 1;
          onProgress(currentPageNum, links.length, Math.min(progress, PROGRESS_PERCENTAGE.CAP));
        });
      }

      try {
        for (let i = 0; i < links.length; i++) {
          const link = links[i];
          const currentPage = i + 1;
          const totalPages = links.length;

          // Calculate progress base for this page (each page contributes 1/totalPages of total progress)
          const pageProgressBase = ((currentPage - 1) / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;
          const pageProgressRange = (1 / totalPages) * PROGRESS_PERCENTAGE.CONVERSION_FACTOR;

          // Helper function to report progress with smooth animation
          const reportProgress = (progressWithinPage: number) => {
            const totalProgress = pageProgressBase + progressWithinPage * pageProgressRange;
            if (globalProgressAnimator) {
              globalProgressAnimator.setTarget(Math.min(totalProgress, PROGRESS_PERCENTAGE.CAP));
            } else {
              onProgress?.(currentPage, totalPages, Math.min(totalProgress, PROGRESS_PERCENTAGE.CAP));
            }
          };

          // Execute crawl to capture DOM elements (always push to keep 1:1 with links)
          const canvas = await this.executeCrawl(link, currentPage, totalPages, reportProgress);
          canvases.push(canvas);
        }
      } finally {
        // Clean up progress animator
        if (globalProgressAnimator) {
          globalProgressAnimator.cleanup();
        }
      }

      // Create final PDF from canvas data with proper element positioning
      Logger.info(`Creating final PDF from ${canvases.length} pages...`);

      // Initialize PDF with fixed dimensions
      this.pdf = new JsPDF({
        format: 'letter',
        orientation: 'landscape',
        compress: true,
      });

      // Load fonts directly from TTF files
      await PdfExporter.loadCustomFonts(this.pdf);

      // Load logo image once for all pages
      try {
        this.pageLogo = await PdfExporter.loadImageAsDataUrl(config.pageLogo);
        this.infoIcon = await PdfExporter.loadImageAsDataUrl(config.infoIcon);
        this.footerLogo = await PdfExporter.loadImageAsDataUrl(config.footerLogo);        
      } catch {
        Logger.warn('Failed loading image assets on PDF export');
      }

      // Generate cover template on the first page
      await this.generateCover(config);

      for (let i = 0; i < canvases.length; i++) {
        const contentCanvas = canvases[i];

        // Add new page for each visited URL (cover is already on the first page)
        this.pdf.addPage();

        // Add page background image to each page
        this.pdf.addImage(config.pageBackgroundImage, IMAGE_SETTINGS.PNG_FORMAT, 0, 0, this.pdf.internal.pageSize.getWidth(), this.pdf.internal.pageSize.getHeight(), 'page-background-image');

        // Generate header template
        this.generateHeader(config, links[i].title);

        // Add content extracted from DOM rendering in an iframe to the page
        if (contentCanvas) {
          const contentImgData = contentCanvas.toDataURL(IMAGE_SETTINGS.PNG_MIME_TYPE, IMAGE_SETTINGS.MAX_QUALITY);

          // Convert canvas dimensions from pixels to millimeters
          const canvasWidthMm = contentCanvas.width / CONVERSION_RATIOS.MM_TO_PX;
          const canvasHeightMm = contentCanvas.height / CONVERSION_RATIOS.MM_TO_PX;

          // Calculate aspect ratio
          const aspectRatio = canvasHeightMm / canvasWidthMm;

          // Fixed width of the image in millimeters
          const fixedWidthMm = IMAGE_SETTINGS.MAX_WIDTH_MM;

          // Calculate height of the image maintaining aspect ratio
          const calculatedHeightMm = fixedWidthMm * aspectRatio;

          // Determine final dimensions maintaining aspect ratio
          let contentWidthMm: number;
          let contentHeightMm: number;

          if (calculatedHeightMm > IMAGE_SETTINGS.MAX_HEIGHT_MM) {
            // If calculated height exceeds max height, use max height and recalculate width
            contentHeightMm = IMAGE_SETTINGS.MAX_HEIGHT_MM;
            contentWidthMm = contentHeightMm / aspectRatio;
          } else {
            // Otherwise, use fixed width and calculated height
            contentWidthMm = fixedWidthMm;
            contentHeightMm = calculatedHeightMm;
          }

          // Calculate content position for injection between header and footer
          const contentXPositionMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
          const contentYPositionMm = TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

          // Content injection as an image (screenshot of the DOM element)
          this.pdf.addImage(
            contentImgData,
            IMAGE_SETTINGS.PNG_FORMAT,
            contentXPositionMm,
            contentYPositionMm,
            contentWidthMm,
            contentHeightMm,
            IMAGE_SETTINGS.PDF_ALIAS,
            IMAGE_SETTINGS.COMPRESSION
          );

          Logger.info(`Added content to page ${i + 1}`);
        } else {
          // Target element not found - show "No data available" where the image would be
          const contentXPositionMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
          const contentYPositionMm = TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;

          this.pdf.setFont('Inter', 'italic');
          this.pdf.setFontSize(14);
          this.pdf.setTextColor(128, 128, 128);
          this.pdf.text('No data available', contentXPositionMm, contentYPositionMm);
          this.pdf.setTextColor(0, 0, 0);

          Logger.info(`Added "No data available" placeholder to page ${i + 1}`);
        }

        // Generate footer template
        this.generateFooter(i + 1, canvases.length, config.firmName, config.allocationStatus, config.isFirmOwned);

        Logger.info(`Completed page ${i + 1}`);
      }

      // Download the final PDF
      const filename = config.filename || 'pdf-export';
      Logger.info(`Downloading final PDF: ${filename}.pdf`);

      try {
        this.pdf.save(`${filename}.pdf`);
        Logger.info('PDF save() method called successfully');
      } catch (saveError) {
        Logger.error('Error calling finalPdf.save():', saveError);
        // Continue anyway - the PDF was created, even if save failed
        // The error might be due to browser restrictions or popup blockers
      }

      // Wait for download to initiate with timeout protection
      // Note: We cannot reliably detect if a browser download actually started
      // due to browser security restrictions, but we can handle errors from save()
      // If the download doesn't start (e.g., blocked by browser), we still report success
      // because the PDF was generated successfully - the download failure is a browser limitation
      await new Promise<void>(resolve => {
        const startTime = Date.now();

        setTimeout(() => {
          const elapsed = Date.now() - startTime;

          // If we waited longer than the maximum expected time, log a warning
          if (elapsed > TIMEOUT_MS.DOWNLOAD_MAX_WAIT) {
            Logger.warn(
              `Wait time (${elapsed}ms) exceeded maximum (${TIMEOUT_MS.DOWNLOAD_MAX_WAIT}ms). ` +
                'Download may have been blocked by browser or popup blocker, but PDF was generated successfully.'
            );
          } else {
            Logger.info('PDF download should have initiated');
          }

          // Report completion - the PDF was generated successfully
          // Note: Even if download was blocked, the PDF creation succeeded
          if (globalProgressAnimator) {
            globalProgressAnimator.setImmediate(PROGRESS_PERCENTAGE.MAX);
          } else {
            onProgress?.(links.length, links.length, PROGRESS_PERCENTAGE.MAX);
          }
          resolve();
        }, TIMEOUT_MS.DOWNLOAD_DELAY);
      });

      Logger.info('Multiple pages export completed successfully');
      return {
        success: true,
        filename: `${filename}.pdf`,
        totalPages: links.length,
        processedPages: canvases.length,
      };
    } catch (error) {
      Logger.error('Error in export:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred during multiple pages export';
      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}
